import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NotificationProvider } from './components/NotificationSystem';
import { ThemeProvider } from './ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import SeoHead from './components/SeoHead';
import './index.css';
import './App.css';

// Lazy load components for better initial performance
const HomePage = lazy(() => import('./components/HomePage'));
const AuctionSetup = lazy(() => import('./components/AuctionSetup'));
const UnifiedDashboard = lazy(() => import('./components/UnifiedDashboard'));
const RegisterPage = lazy(() => import('./components/RegisterPage'));
const RegistrationsAdmin = lazy(() => import('./components/RegistrationsAdmin'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const PublicAuctionPage = lazy(() => import('./components/PublicAuctionPage'));
const TournamentsDirectory = lazy(() => import('./components/TournamentsDirectory'));
const DemoPage = lazy(() => import('./components/DemoPage'));
const StreamingOverlay = lazy(() => import('./components/StreamingOverlay'));
const TeamBidderPage = lazy(() => import('./components/TeamBidderPage'));
const HelpCenter = lazy(() => import('./components/HelpCenter'));
const PrivacyPage = lazy(() =>
  import('./components/LegalPages').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() =>
  import('./components/LegalPages').then((m) => ({ default: m.TermsPage }))
);

// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center gbx-bg">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto mb-4"></div>
      <p className="text-amber-100 text-lg">Loading GoldenBidX...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <ThemeProvider>
          <Router>
            <SeoHead />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Home page with login options */}
                <Route path="/" element={<HomePage />} />

                {/* NEW: Auction setup for admin */}
                <Route path="/setup" element={<AuctionSetup />} />

                {/* Unified dashboard for both admin and spectators */}
                <Route path="/dashboard" element={<UnifiedDashboard />} />

                {/* Public player registration form */}
                <Route path="/register/:slug" element={<RegisterPage />} />

                {/* Public view-only live auction */}
                <Route path="/a/:slug" element={<PublicAuctionPage />} />

                {/* Streaming/OBS overlay graphic (transparent, current player + bid only) */}
                <Route path="/a/:slug/overlay" element={<StreamingOverlay />} />

                {/* Team owner remote bidding (opt-in per auction; code-gated) */}
                <Route path="/bid/:auctionId" element={<TeamBidderPage />} />

                {/* Public tournaments directory */}
                <Route path="/tournaments" element={<TournamentsDirectory />} />

                {/* Shareable pricing link → homepage pricing section */}
                <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />

                {/* Auto-playing product demo (sample data) */}
                <Route path="/demo" element={<DemoPage />} />

                {/* Legal (also required by the Google sign-in consent screen) */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />

                {/* Video guides, tours, FAQ & contact */}
                <Route path="/help" element={<HelpCenter />} />

                {/* Organizer / admin dashboard (renamed from /registrations) */}
                <Route path="/console" element={<RegistrationsAdmin />} />
                <Route path="/registrations" element={<Navigate to="/console" replace />} />

                {/* Public password reset via emailed token */}
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Router>
        </ThemeProvider>
      </NotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
